/**
 * AI CONTEXT
 * Purpose: curriculum plan CRUD table page.
 * Key deps: useCurriculumTableData, TableFiltersRow, AddCurriculumFormModal.
 * Referenced by: App route ROUTES.CURRICULUM_PLANS.
 * Importance: curricula define class structure/progression.
 * Refactor: reuse generic CRUD table wrapper; tighten permission empty states.
 */
import { useMemo, useState } from "react"

import EmptyPage from "#blocks/EmptyPage"
import Flex from "#blocks/Flex"
import Page from "#blocks/Page"
import { buildTableColumns } from "#blocks/Table/buildTableColumns"
import Table2 from "#blocks/Table/Table2"
import TableFiltersRow from "#blocks/Table/TableFiltersRow"
import {
  CURRICULUM_SORTABLE_FIELDS,
  CURRICULUM_TABLE_COLUMNS,
  getCurriculumSortValue,
} from "#constants/curriculumOptions"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import { type CurriculumForTable } from "#customTypes/composedTypes/curriculumTypes"
import { SETTINGS_KEYS, type Uuid } from "#customTypes/globalTypes"
import AddCurriculumFormModal from "#forms/AddCurriculum/AddCurriculumFormModal"
import useCurriculumTableData from "#hooks/dataHooks/useCurriculumTableData"
import useHasPermission from "#hooks/newHooks/useHasPermission"

const CurriculumsTable = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedCurriculumId, setSelectedCurriculumId] = useState<Uuid | null>(
    null
  )

  const canReadCurriculums = useHasPermission(
    RESOURCES.CURRICULUM_PLAN,
    USER_ACTIONS.READ
  )
  const canViewId = useHasPermission(RESOURCES.ID, USER_ACTIONS.READ)

  const {
    curriculumsForTable,
    isReady,
    errors,
    onSearch,
    sortField,
    sortOrder,
    onSortChange,
  } = useCurriculumTableData()

  const columns = useMemo(
    () =>
      buildTableColumns<CurriculumForTable>(CURRICULUM_TABLE_COLUMNS, {
        sortableFields: CURRICULUM_SORTABLE_FIELDS,
        getSortValue: getCurriculumSortValue,
        showId: canViewId,
      }),
    [canViewId]
  )

  const handleOpen = () => {
    if (!canReadCurriculums) {
      return
    }

    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedCurriculumId(null)
  }

  const handleRowClick = (selectedCurriculumId: Uuid): void => {
    setSelectedCurriculumId(selectedCurriculumId)
    handleOpen()
  }

  if (!canReadCurriculums) {
    return (
      <Page>
        <EmptyPage />
      </Page>
    )
  }

  return (
    <Page>
      <Flex col style={{ padding: 20, width: "100%", maxWidth: 2200 }} gap={12}>
        <h1>Curriculums</h1>

        <TableFiltersRow
          onAdd={handleOpen}
          onSearch={onSearch}
          searchPlaceholder="Search by name, group, skill levels"
          settingsKey={SETTINGS_KEYS.CURRICULUMS}
        />

        {isOpen && (
          <AddCurriculumFormModal
            onClose={handleClose}
            curriculumId={selectedCurriculumId}
          />
        )}
        <Table2<CurriculumForTable>
          isLoading={!isReady}
          errors={errors}
          data={curriculumsForTable}
          columns={columns}
          onRowClick={(selectedCurriculumId) => {
            void handleRowClick(selectedCurriculumId)
          }}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={onSortChange}
        />
      </Flex>
    </Page>
  )
}

export default CurriculumsTable
